import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { userDisplayNameQuery, useSupervisionMeetingQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { Loader, Modal, ModalContent, ModalTitle } from '@uplift/ui-v2';
import { useSuspenseQueries } from '@tanstack/react-query';


const Section: React.FC<{ label: string }> = ({ label, children }) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="overline" color="secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
      {label}
    </Typography>
    {children}
  </Box>
);


export type SupervisionMeetingDetailModalProps = {
  open: boolean;
  onClose: () => void;
  onClosed?: () => void;
  meetingId: string;
  providerId: string;
};

const SupervisionMeetingDetailModalContent: React.VFC<Omit<SupervisionMeetingDetailModalProps, 'open' | 'onClose' | 'onClosed'>> = ({ meetingId, providerId }) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { data: meeting } = useSupervisionMeetingQuery(providerId, meetingId);
  const [
    { data: physicianDisplayName },
    { data: providerDisplayName }
  ] = useSuspenseQueries({
    queries: [
      userDisplayNameQuery(meeting.collaborativePhysicianId),
      userDisplayNameQuery(meeting.superviseeId)
    ]
  });

  const submittedAt = meeting?.submittedAt ? CalendarDateService.date(meeting.submittedAt).tz(timezone) : null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Supervision Meeting</Typography>
        <Typography sx={{ fontWeight: 600 }}>with {providerDisplayName?.fullWithAffixes}</Typography>
      </Box>
      <Divider />

      <Section label="Date of Meeting">
        <Typography>{CalendarDateService.date(meeting.meetingDate).tz(timezone).format(DateFormats.DATETIME)}</Typography>
      </Section>
      <Divider />

      <Section label="Participants">
        <Typography sx={{ fontWeight: 500 }}>Supervising physician: {physicianDisplayName?.fullWithAffixes}</Typography>
        <Typography sx={{ fontWeight: 500 }}>Supervisee: {providerDisplayName?.fullWithAffixes}</Typography>
        <Typography sx={{ fontWeight: 500 }}>Method of communication: {l10n.supervisees.meetingMethods[meeting.method]}</Typography>
      </Section>
      <Divider />

      <Section label="Duration">
        <Typography>{meeting.durationMinutes} minutes</Typography>
      </Section>
      <Divider />

      <Section label="Meeting Summary">
        <Typography>{meeting.summary}</Typography>
      </Section>

      {submittedAt?.isValid() && (
        <Typography sx={{ mt: 3 }} color="text.secondary">
          Signed and submitted by {physicianDisplayName?.fullWithAffixes} on {CalendarDateService.format(submittedAt, DateFormats.DATETIME)}
        </Typography>
      )}
    </>
  );
};


export const SupervisionMeetingDetailModal: React.VFC<SupervisionMeetingDetailModalProps> = ({ open, onClose, onClosed, meetingId, providerId }) => {
  return (
    <Modal open={open} maxWidth="md" fullWidth onClose={onClose} onExited={onClosed}>
      <ModalTitle onClose={onClose} />
      <ModalContent>
        <React.Suspense fallback={<Loader height="502px" />}>
          <SupervisionMeetingDetailModalContent meetingId={meetingId} providerId={providerId} />
        </React.Suspense>
      </ModalContent>
    </Modal>
  );
};
